import React, { useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { isDefined } from "@utils/helpers";
import { getInvalidationsForComponent } from "@utils/search/invalidation.utils";

import { toHierarchicalFacetPath } from "@services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";

import { useThunkDispatch } from "@redux/dispatcher";
import {
    clearHierarchicalFacet,
    selectHierarchicalFacetValue,
} from "@redux/modules/value/search/search.actions";
import {
    getFilter,
    getSelectedValueForHierarchicalFacet,
} from "@redux/modules/value/search/search.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
import { FacetComponentProps } from "@components/Search/SearchResults/StateComponents/Facets/Facet/@types/facet.types";
import { FacetType } from "@components/Search/SearchResults/StateComponents/Facets/Facet/@types/facetType.types";
import { HierarchicalFacetComponent } from "@components/Search/SearchResults/StateComponents/Facets/Facet/components/HierarchicalFacet/hierarchicalFacet.component";
import {
    facetValuesOfCategoryPageId,
    getShowAllCategories,
} from "@components/Search/SearchResults/StateComponents/Facets/Facet/components/HierarchicalFacet/hierarchicalFacet.helpers";

import { CATEGORY_PAGE_IDS } from "../../facet.configuration";

const CATEGORY_PAGEIDS_REGEX = new RegExp(`'${CATEGORY_PAGE_IDS}':'(.*?)'`);

const HierarchicalFacet = ({
    facet,
    component,
    sendEvent,
}: FacetComponentProps<FacetType.HIERARCHICAL>) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const filters = useSelector(getFilter);
    const categoryPageIdFilter = filters?.match(CATEGORY_PAGEIDS_REGEX)?.at(1);

    const facetValues = useMemo(
        () => facetValuesOfCategoryPageId(facet, categoryPageIdFilter),
        [facet, categoryPageIdFilter],
    );

    const selectedValue = useSelector(
        getSelectedValueForHierarchicalFacet(component, facet.name),
        shallowEqual,
    );

    const selectedValueLevel = isDefined(selectedValue)
        ? toHierarchicalFacetPath(selectedValue).length
        : undefined;

    const invalidations = useMemo(
        () => getInvalidationsForComponent(component),
        [component],
    );

    const refine = useCallback(
        (level: number, value: string) =>
            dispatch(
                selectHierarchicalFacetValue(
                    component,
                    facet.name,
                    value,
                    level,
                    invalidations,
                    sendEvent,
                ),
            ),
        [component, invalidations, dispatch, facet.name],
    );

    const reset = useCallback(
        () =>
            dispatch(
                clearHierarchicalFacet(component, facet.name, invalidations),
            ),
        [component, invalidations, dispatch, facet.name],
    );

    const showAllCategories = getShowAllCategories(
        selectedValue,
        categoryPageIdFilter,
    );

    return (
        <>
            {selectedValue && showAllCategories && (
                <Button
                    onClick={reset}
                    type={"button"}
                    color={"link"}
                    className={"px-0 text-decoration-none"}
                >
                    <i className={"las la-angle-left mr-1"}></i>
                    {t("search.refinements.categories.allCategories")}
                </Button>
            )}
            <HierarchicalFacetComponent
                key={selectedValue}
                childValues={facetValues}
                level={0}
                refine={refine}
                selectedValue={selectedValue}
                selectedValueLevel={selectedValueLevel}
            />
        </>
    );
};

export { HierarchicalFacet };
